import { Link } from "react-router-dom";
import Layout from "../layout/Layout";
import SEO from "../Components/SEO/SEO";

export default function PrivacyPolicy() {
  return (
    <Layout>
      <SEO
        title="Privacy Policy | Quantum Spark Technology"
        description="How Quantum Spark Technology collects, uses, and protects personal information on quantumsparktechnology.com."
        path="/privacy"
      />
      <article className="mx-auto max-w-3xl px-6 py-16 md:py-24">
        <p className="mb-4 text-sm font-semibold uppercase tracking-widest text-brand-purple">Legal</p>
        <h1 className="mb-4 font-display text-4xl font-bold text-brand-ink md:text-5xl">Privacy Policy</h1>
        <p className="mb-10 text-sm text-brand-muted">Last updated: September 2, 2026</p>

        <div className="prose-legal space-y-8 text-base leading-relaxed text-brand-muted">
          <section>
            <h2 className="mb-3 font-display text-2xl font-bold text-brand-ink">1. Who we are</h2>
            <p>
              Quantum Spark Technology (&ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;) operates quantumsparktechnology.com
              and provides custom software development services including websites, mobile apps, UI/UX design, AI
              integrations, blockchain solutions, and quality assurance. This Privacy Policy explains what information we
              collect, how we use it, and the choices you have.
            </p>
          </section>

          <section>
            <h2 className="mb-3 font-display text-2xl font-bold text-brand-ink">2. Information we collect</h2>
            <p className="mb-3">We may collect the following categories of information:</p>
            <ul className="list-disc space-y-2 pl-5">
              <li>
                <strong className="text-brand-ink">Contact details</strong> you submit through forms, email, or chat —
                such as name, work email, company name, phone number, and project description.
              </li>
              <li>
                <strong className="text-brand-ink">Job application data</strong> submitted via our Careers page —
                including name, email, phone, role applied for, portfolio links, cover note, and resume/CV files.
                Applications are used only to evaluate hiring fit and to contact candidates.
              </li>
              <li>
                <strong className="text-brand-ink">Usage data</strong> such as pages visited, approximate location
                derived from IP address, browser type, device type, and referral source.
              </li>
              <li>
                <strong className="text-brand-ink">Cookies and similar technologies</strong> used for analytics,
                advertising (including Google AdSense where enabled), and remembering preferences.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="mb-3 font-display text-2xl font-bold text-brand-ink">3. How we use information</h2>
            <ul className="list-disc space-y-2 pl-5">
              <li>Respond to inquiries and prepare project proposals</li>
              <li>Deliver contracted software development and support services</li>
              <li>Improve website performance, content quality, and user experience</li>
              <li>Measure traffic and, where permitted, show relevant advertising</li>
              <li>Comply with legal obligations and protect against fraud or abuse</li>
            </ul>
          </section>

          <section>
            <h2 className="mb-3 font-display text-2xl font-bold text-brand-ink">4. Cookies, analytics, and advertising</h2>
            <p className="mb-3">
              When you first visit, we show a cookie consent banner. Essential cookies needed for the site to work do
              not require consent. Analytics and advertising cookies (Google Analytics, Vercel Analytics, and Google
              AdSense where enabled) load only after you accept them. You can accept all, reject non-essential cookies,
              or save a custom choice. Your preference is stored in your browser so we remember it on later visits.
            </p>
            <p className="mb-3">
              Third-party vendors, including Google, may use cookies to serve ads based on prior visits to this or other
              websites. You can also manage cookies in your browser and opt out of personalized advertising via
              Google&rsquo;s Ads Settings. To change your site cookie choice later, clear site data for this domain or
              contact us and we can guide you.
            </p>
            <p>
              We do not knowingly sell personal information. Advertising partners may receive limited technical data
              needed to deliver and measure ads according to their own privacy policies. Browsing alone does not give us
              your name or email address.
            </p>
          </section>

          <section>
            <h2 className="mb-3 font-display text-2xl font-bold text-brand-ink">5. How we share information</h2>
            <p>
              We share personal information only with service providers who help us operate the business (for example
              hosting, email delivery, analytics, or advertising), when required by law, or with your consent. We do not
              sell your contact details to unrelated third parties.
            </p>
          </section>

          <section>
            <h2 className="mb-3 font-display text-2xl font-bold text-brand-ink">6. Data retention and security</h2>
            <p>
              We retain inquiry and project records for as long as needed to provide services, resolve disputes, and meet
              legal requirements. We use reasonable administrative and technical safeguards. No method of transmission
              over the internet is fully secure; please avoid sending sensitive credentials through contact forms.
            </p>
          </section>

          <section>
            <h2 className="mb-3 font-display text-2xl font-bold text-brand-ink">7. Your rights</h2>
            <p>
              Depending on your location, you may request access, correction, deletion, or restriction of your personal
              data, or object to certain processing. To exercise these rights, email us at the address below. We will
              respond within a reasonable timeframe.
            </p>
          </section>

          <section>
            <h2 className="mb-3 font-display text-2xl font-bold text-brand-ink">8. Children&rsquo;s privacy</h2>
            <p>
              Our services are directed to businesses and professionals. We do not knowingly collect personal information
              from children under 13 (or the equivalent minimum age in your jurisdiction).
            </p>
          </section>

          <section>
            <h2 className="mb-3 font-display text-2xl font-bold text-brand-ink">9. Changes to this policy</h2>
            <p>
              We may update this Privacy Policy from time to time. The &ldquo;Last updated&rdquo; date at the top will
              change when we publish revisions. Continued use of the site after updates constitutes acceptance of the
              revised policy.
            </p>
          </section>

          <section>
            <h2 className="mb-3 font-display text-2xl font-bold text-brand-ink">10. Contact</h2>
            <p>
              Questions about privacy:{" "}
              <a className="font-medium text-brand-purple hover:underline" href="mailto:info@quantumsparktechnology.com">
                info@quantumsparktechnology.com
              </a>
              . Or visit our{" "}
              <Link className="font-medium text-brand-purple hover:underline" to="/contact">
                contact page
              </Link>
              .
            </p>
          </section>
        </div>
      </article>
    </Layout>
  );
}
